import React, {useState} from 'react';
import { View, Pressable, Text, Dimensions } from 'react-native';
import styled from 'styled-components/native';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useQuery } from '@apollo/client';
import * as Colors from './colors';

import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
  LinkSpace,
} from 'app/src/elements';
import { elGray, elLightGray, elRed } from 'app/src/styles/colors';
import SellerSelect from './SellerSelect'
import AssetSelect from './AssetSelect'
import DateRangeSelect from './DateRangeSelect';
import LimitDatesSelect from './LimitDatesSelect';
import ShowSoldSelect from './ShowSoldSelect';
import Content from './ContentBox';
import { VALUE_DASHBOARD } from './queries'
import DataState from 'app/src/components/DataState';
import { currency, numberWithDelimiter } from 'app/src/utils/stringHelpers';
import Modal from 'app/src/elements/Modal';
import FeeTable from './FeeTable';

const dimensions = Dimensions.get('window');
const smallScreen = dimensions.width < 800;

const Dashboard = () => {
  const today = DateTime.now().toISODate();
  const startOfMonth = DateTime.now().startOf('month')

  const [seller, setSeller] = useState(null);
  const [asset, setAsset] = useState(null);
  const [limitDates, setLimitDates] = useState(false);
  const [showOnlySold, setShowOnlySold] = useState(false);
  const [startOn, setStartOn] = useState(startOfMonth);
  const [endOn, setEndOn] = useState(today);
  const [showModal, setShowModal] = useState(null);

  const { data, loading, error } = useQuery(VALUE_DASHBOARD, {
    variables: { startOn, endOn, sellerId: seller?.id, assetId: asset?.id, feesOnDates: limitDates, showOnlySold: showOnlySold }
  });

  if (error) return <DataState.Error error={error} />;

  const resetDashboard = () => {
    setSeller(null);
    setAsset(null);
  }

  return (
    <MainContainer style={{fontFamily: "Arial", marginTop: '-25px'}}>
      <ContentContainer>
        <PageTitle>Dashboard</PageTitle>
        <View style={{ flexDirection: 'row', gap: 16, zIndex: 1010, marginTop: '15px'}}>
          <Pressable onPress={() => resetDashboard()} style={{ flex: 1 }}>
            <Content.ValueBox highlight={!seller}>
              <Content.Value color={seller ? null : 'white'}>
                Company Wide
              </Content.Value>
            </Content.ValueBox>
          </Pressable>

          <SellerSelect
            selectedSeller={seller}
            setSeller={setSeller}
            setAsset={setAsset}
          />

          {seller ? (
             <AssetSelect
              selectedSeller={seller}
              selectedAsset={asset}
              setAsset={setAsset}
            />
          ) : (
            <Content.ValueBox style={{ flex: 1 }}>
              <Content.Value>Choose a Seller to Choose an Asset</Content.Value>
            </Content.ValueBox>
          )}
        </View>
        <View style={{ flexDirection: 'row', gap: 16, zIndex: 1000, marginTop: '8px' }}>
          <DateRangeSelect
            startOn={startOn}
            setStartOn={setStartOn}
            endOn={endOn}
            setEndOn={setEndOn}
          />
          <LimitDatesSelect
            limitDates={limitDates}
            setLimitDates={setLimitDates}
          />
          <ShowSoldSelect
            showOnlySold={showOnlySold}
            setShowOnlySold={setShowOnlySold}
          />
        </View> 

         {(data && !loading) ? (
        <>
          <Content.Container style={{ marginTop: 24 }}>
            <Content.Column style={{gap: 0}}>
              <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardAqua, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {numberWithDelimiter(data.valueDashboard.assetsListed.length)}
                </Text>
                Assets Listed
              </Content.Box>
            </Content.Column>
            <Content.Column>
              <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardGreen, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {numberWithDelimiter(data.valueDashboard.assetsSold.length)}
                </Text>
                Assets Sold<br />
                {data.valueDashboard.soldPercentage} Sold
              </Content.Box>
            </Content.Column>
            <Content.Column>
              <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardRed, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {numberWithDelimiter(data.valueDashboard.reruns)}
                </Text>
                Assets from this time-period with more than one run
              </Content.Box>
            </Content.Column>
            <Content.Column>
              <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardRed, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {numberWithDelimiter(data.valueDashboard.totalAverageRuns)}
                </Text>
                Average number of runs based on {numberWithDelimiter(data.valueDashboard.totalAssetsWithOneRun)} assets <b>all-time</b>
              </Content.Box>
            </Content.Column>
          </Content.Container>

          <Content.Container style={{ marginTop: 24 }}>
            <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardGray, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {currency(data.valueDashboard.salePrice)}
                </Text>
                Asset Sale Price  
              </Content.Box>
            </Content.Column>
            <Content.Column>
              <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardGreen, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {currency(data.valueDashboard.totalFees)}
                </Text>
                Fee Income
              </Content.Box>
            </Content.Column>
            <Content.Column>
              <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardAltGreen, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {currency(data.valueDashboard.totalFeesSold)}
                </Text>
                Fee Income on Assets Sold During the Period
              </Content.Box>
            </Content.Column>
          </Content.Container>

          <Content.Container style={{ marginTop: 24 }}>
            <Content.Column>
              <Pressable onPress={() => setShowModal(data.valueDashboard.buyFees)}>
                <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardGreen, color: 'white', textAlign: 'center', gap: 0 }}>
                  <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                    {numberWithDelimiter(data.valueDashboard.buyFeesCount)} Buy Fees
                  </Text>
                  Fee Income: {currency(data.valueDashboard.buyFeesAmount)}<br />
                  Expenses: ({currency(data.valueDashboard.buyFeesExpenses)})
                  <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                    Net Income: {currency(data.valueDashboard.buyFeesNet)}
                  </Text>
                </Content.Box>
              </Pressable>
            </Content.Column>
            <Content.Column>
              <Pressable onPress={() => setShowModal(data.valueDashboard.sellFees)}>
                <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardYellow, color: 'white', textAlign: 'center', gap: 0 }}>
                  <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                    {numberWithDelimiter(data.valueDashboard.sellFeesCount)} Sell Fees
                  </Text>
                  Fee Income: {currency(data.valueDashboard.sellFeesAmount)}<br />
                  Expenses: ({currency(data.valueDashboard.sellFeesExpenses)})
                  <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                    Net Income: {currency(data.valueDashboard.sellFeesNet)}
                  </Text>
                </Content.Box>
              </Pressable>
            </Content.Column>
            <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardOrange, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.sellFeesPercentage}
                </Text>
                Sell Fee percentage of total sales during the period.
              </Content.Box>
            </Content.Column>
          </Content.Container>

          <Content.Container style={{ marginTop: 24 }}>
            <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardAqua, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.transFeesCount} Transport Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.transFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.transFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.transFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
            <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardLine2, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.keyFeesCount} Key Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.keyFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.keyFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.keyFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
             <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardLine3, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.reconFeesCount} Recon Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.reconFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.reconFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.reconFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
             <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardLine4, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.mechFeesCount} Mech Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.mechFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.mechFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.mechFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
             <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardLine5, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.insFeesCount} Inspection Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.insFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.insFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.insFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
          </Content.Container>

          <Content.Container style={{ marginTop: 24 }}>
            <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardAqua, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.otherFeesCount} Other Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.otherFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.otherFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.otherFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
            <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardLine2, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.listFeesCount} Listing Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.listFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.listFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.listFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
             <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardLine3, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.storageFeesCount} Storage Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.storageFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.storageFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.storageFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
             <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardLine4, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.bailoutFeesCount} Bailout Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.bailoutFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.bailoutFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.bailoutFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
             <Content.Column>
               <Content.Box style={{ height: '100%', width: '100%', borderRadius: '10px', backgroundColor: Colors.dashboardLine5, color: 'white', textAlign: 'center', gap: 0 }}>
                <Text style={{fontSize: '1.2em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  {data.valueDashboard.finFeesCount} Finance Charge Fees
                </Text>
                Fee Income: {currency(data.valueDashboard.finFeesAmount)}<br />
                Expenses: ({currency(data.valueDashboard.finFeesExpenses)})<br />
                <Text style={{fontSize: '1.6em', fontWeight: 'bold', color: 'white', textAlign: 'center'}}>
                  Net: {currency(data.valueDashboard.finFeesNet)}
                </Text>
              </Content.Box>
            </Content.Column>
          </Content.Container>
          {showModal &&
            <Modal
              showModal={showModal}
              setShowModal={setShowModal}
              modalType={'View Fee Details'}
            >
              <FeeTable fees={showModal} />
            </Modal>
          }
        </>
      ) : (
        <DataState.Loading />
      )}
      </ContentContainer>
    </MainContainer>
  )
}

export default Dashboard;